import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

// Types for chatbot API
export type Measure = {
  id: number
  name: string
  description?: string
  category?: string
}

export type MeasureRecommendation = {
  measure: Measure
  confidence: number
  reasoning: string
}

export type UpsellOpportunity = {
  missingMeasure: Measure
  reasoning: string
  packageName?: string
}

export type QueryResult = {
  explanation: string
  confidence: number
  primaryRecommendations: MeasureRecommendation[]
  secondaryRecommendations?: MeasureRecommendation[]
  upsellOpportunities?: UpsellOpportunity[]
  safetyNote?: string
}

export type MeasureScore = {
  measureId: number
  measureName: string
  score: number
  benchmarkScore: number
  percentile: number
}

export type PersonMatch = {
  userId: number
  firstName: string
  lastName: string
  email: string
  role?: string
  overallScore: number
  matchReason: string
  strengths: string[]
  measureScores?: MeasureScore[]
}

export type ChatbotQueryRequest = {
  query: string
  organizationId: number
}

export type ChatbotQueryResponse = {
  success: boolean
  message?: string
  data: QueryResult | {
    contactSupportRequired?: boolean
    supportMessage?: string
  }
}

export type PeopleMatchRequest = {
  measureIds: number[]
  organizationId: number
  filters?: {
    maxResults?: number
    sortBy?: "score" | "name"
    minScore?: number
  }
}

export type PeopleMatchResponse = {
  success: boolean
  message?: string
  data: {
    matches: PersonMatch[]
    totalCount: number
  }
}

export type OrganizationMeasuresResponse = {
  success: boolean
  data: {
    measures: Measure[]
    categories: string[]
  }
}

export type FeedbackRequest = {
  queryId?: string
  query: string
  rating: "helpful" | "not_helpful"
  comment?: string
  organizationId: number
}

// Base URL from environment or fallback to staging
const baseUrl = process.env.REACT_APP_API_URL || "https://stageapi-simulations.meseekna.com"

// Get token from localStorage for API requests
const getToken = () => {
  try {
    const auth = JSON.parse(localStorage.getItem("persist:msk:auth") || "{}")
    const user = JSON.parse(auth.user || "{}")
    return user.token || null
  } catch (e) {
    console.error("Error getting user token:", e)
    return null
  }
}

export const chatbotApi = createApi({
  reducerPath: "chatbotApi",
  baseQuery: fetchBaseQuery({
    baseUrl,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = getToken()
      if (token) {
        headers.set("authorization", `Bearer ${token}`)
      }
      headers.set("content-type", "application/json")
      return headers
    },
  }),
  tagTypes: ["ChatbotQuery", "OrganizationMeasures"],
  endpoints: (builder) => ({
    // Process a natural language query
    processQuery: builder.mutation<ChatbotQueryResponse, ChatbotQueryRequest>({
      query: (body) => ({
        url: "/saas/chatbot/query",
        method: "POST",
        body,
      }),
    }),

    // Find people matching recommended measures
    findPeopleMatches: builder.mutation<PeopleMatchResponse, PeopleMatchRequest>({
      query: (body) => ({
        url: "/saas/chatbot/people-match",
        method: "POST",
        body,
      }),
    }),

    // Get available measures for an organization
    getOrganizationMeasures: builder.query<OrganizationMeasuresResponse, number>({
      query: (organizationId) => ({
        url: `/saas/chatbot/organization-measures/${organizationId}`,
      }),
      providesTags: ["OrganizationMeasures"],
    }),

    // Submit feedback on chatbot recommendations
    submitFeedback: builder.mutation<{ success: boolean }, FeedbackRequest>({
      query: (body) => ({
        url: "/saas/chatbot/feedback",
        method: "POST",
        body,
      }),
    }),
  }),
})

export const {
  useProcessQueryMutation,
  useFindPeopleMatchesMutation,
  useGetOrganizationMeasuresQuery,
  useSubmitFeedbackMutation,
} = chatbotApi
